import moment from 'moment';
import withParams from 'vuelidate/lib/withParams';
import { documentsWithExpiry } from '@/features/selectors';

const validationPatterns = {
  cyrillicSeries: /^[А-ЩЬЮЯҐЄІЇ]{2}$/i,
  idRecord: /^\d{8}-\d{5}$/,
  digitsOnly: /^\d+$/,
  latinAlphanumeric: /^[A-Z0-9]+$/i,
  flexibleText: /^[а-яіїєґa-z0-9\s.,/'-]+$/i
};

export const DocumentValidation = () => {
  const validSeries = (documentId) => withParams({ validSeries: true }, (value) => {
    if (!value) return true;

    if (!documentId) return false;

    const val = value.trim();

    switch (documentId) {
      case 'DOCUMENT_PASSPORT':
        return validationPatterns.cyrillicSeries.test(val);
      case 'DOCUMENT_ID_CARD':
      case 'DOCUMENT_ID_RESIDENCE_PERMIT':
        return validationPatterns.idRecord.test(val);
      case 'DOCUMENT_EXTERNAL_PASSPORT':
      case 'DOCUMENT_DRIVERS_LICENSE':
        return validationPatterns.latinAlphanumeric.test(val);
      default:
        return validationPatterns.flexibleText.test(val);
    }
  });

  const validNumber = (documentId) => withParams({ validNumber: true }, (value) => {
    if (!value) return true;

    if (!documentId) return false;

    const val = value.trim();

    switch (documentId) {
      case 'DOCUMENT_PASSPORT':
      case 'DOCUMENT_DRIVERS_LICENSE':
        return validationPatterns.digitsOnly.test(val) && val.length === 6;
      case 'DOCUMENT_ID_CARD':
      case 'DOCUMENT_ID_RESIDENCE_PERMIT':
        return validationPatterns.digitsOnly.test(val) && val.length === 9;
      case 'DOCUMENT_EXTERNAL_PASSPORT':
        return validationPatterns.latinAlphanumeric.test(val) && val.length >= 6;
      default:
        return val.length >= 2;
    }
  });

  const parseDate = (value) => {
    if (!value) return null;
    if (value instanceof Date) return moment(value);
    return moment(value, ['DD.MM.YYYY', 'DD-MM-YYYY', 'YYYY-MM-DD'], true);
  };

  const validIssueDate = (documentId, policyholderBirthday) => withParams({ validIssueDate: true }, (value) => {
    if (!value) return true;

    const issue = parseDate(value);
    if (!issue || !issue.isValid() || issue.isAfter(moment(), 'day')) return false;

    const minAge = documentId === 'DOCUMENT_ID_CARD'
      ? 14
      : documentId === 'DOCUMENT_PASSPORT' ? 16 : null;

    if (minAge !== null) {
      if (!policyholderBirthday) {
        return moment().diff(issue, 'years', true) >= minAge;
      }
      const birth = parseDate(policyholderBirthday);
      if (!birth || !birth.isValid()) return true;
      return issue.diff(birth, 'years') >= minAge;
    }

    return true;
  });

  const isExpiryRequired = (typeId) => documentsWithExpiry.includes(typeId);

  const validExpiryDate = (documentId, issueDate, isRequired = isExpiryRequired(documentId)) => withParams({ validExpiryDate: true }, (value) => {
    if (!value) return !isRequired;

    const expiry = parseDate(value);
    if (!expiry || !expiry.isValid()) return false;

    const isFuture = expiry.isSameOrAfter(moment(), 'day');

    if (issueDate) {
      const issue = parseDate(issueDate);
      if (issue && issue.isValid() && !expiry.isAfter(issue)) return false;
    }

    return isFuture;
  });

  const validIssuingAuthority = withParams({ validIssuingAuthority: true }, (value) => {
    if (!value) return true;
    return value.trim().length >= 2 && validationPatterns.flexibleText.test(value);
  });

  return {
    validSeries,
    validNumber,
    validIssueDate,
    validExpiryDate,
    validIssuingAuthority,
  };
};
